import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "motion/react";
import { PageHeader } from "@/components/page-header";
import { SiteFooter } from "@/components/site-footer";

type Section = {
  title: string;
  subtitle: string;
  description: string;
  to: string;
  params?: Record<string, string>;
  cta: string;
  numeral: string;
  sigil: "sprig" | "flask" | "moon" | "leaf";
};

const sections: Section[] = [
  {
    title: "Natural Perfumery Primer",
    subtitle: "The Herbal Codex",
    description:
      "A guide to natural perfume: materials, methods, rituals, and mysteries.",
    to: "/field-notes/$blog",
    params: { blog: "the-herbal-codex" },
    cta: "Read the primer",
    numeral: "№ I",
    sigil: "flask",
  },
  {
    title: "Materia Botanica",
    subtitle: "Apothecary Profiles",
    description:
      "Ingredient portraits: roots, resins, flowers, woods, and other opinionated substances.",
    to: "/field-notes/$blog",
    params: { blog: "materia-botanica" },
    cta: "Enter the apothecary",
    numeral: "№ II",
    sigil: "sprig",
  },
  {
    title: "Bottle Mythologies",
    subtitle: "Fragrance Stories",
    description:
      "The stories, symbols, and strange weather behind each perfume.",
    to: "/field-notes/$blog",
    // Shopify blog handle unchanged — only the displayed label is "Bottle Mythologies".
    params: { blog: "perfume-chapters" },
    cta: "Read the mythologies",
    numeral: "№ III",
    sigil: "moon",
  },
  {
    title: "Character Archive",
    subtitle: "Notes as Personas",
    description:
      "A cabinet of botanical characters: saints, tricksters, ghosts, and minor villains in aromatic form.",
    to: "/character-archive",
    cta: "Open the archive",
    numeral: "№ IV",
    sigil: "leaf",
  },
];

function Sigil({ kind }: { kind: Section["sigil"] }) {
  const common = {
    width: 22,
    height: 22,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    className: "opacity-40",
    "aria-hidden": true,
  };
  switch (kind) {
    case "sprig":
      return (
        <svg {...common}>
          <path d="M12 21V6" />
          <path d="M12 11c-3 0-5-2-5-5 3 0 5 2 5 5z" />
          <path d="M12 14c3 0 5-2 5-5-3 0-5 2-5 5z" />
          <path d="M12 18c-2 0-4-1-4-3 2 0 4 1 4 3z" />
        </svg>
      );
    case "flask":
      return (
        <svg {...common}>
          <path d="M9 3h6" />
          <path d="M10 3v5l-4 9a3 3 0 0 0 3 4h6a3 3 0 0 0 3-4l-4-9V3" />
          <path d="M8 14h8" />
        </svg>
      );
    case "moon":
      return (
        <svg {...common}>
          <path d="M20 14a8 8 0 1 1-10-10 6 6 0 0 0 10 10z" />
          <circle cx="16" cy="9" r="0.6" fill="currentColor" />
        </svg>
      );
    case "leaf":
      return (
        <svg {...common}>
          <path d="M4 20c0-9 7-16 16-16 0 9-7 16-16 16z" />
          <path d="M4 20c5-5 8-8 16-16" />
        </svg>
      );
  }
}

export const Route = createFileRoute("/field-notes/")({
  head: () => ({
    meta: [
      { title: "Field Notes — House of Apocrypha" },
      {
        name: "description",
        content:
          "Field Notes from House of Apocrypha — a primer on natural perfumery, ingredient portraits, bottle mythologies, and the character archive.",
      },
      { property: "og:title", content: "Field Notes — House of Apocrypha" },
      {
        property: "og:description",
        content:
          "A primer on natural perfumery, ingredient portraits, bottle mythologies, and the character archive.",
      },
      { property: "og:url", content: "https://houseofapocrypha.com/field-notes" },
    ],
    links: [{ rel: "canonical", href: "https://houseofapocrypha.com/field-notes" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "Field Notes — House of Apocrypha",
          url: "https://houseofapocrypha.com/field-notes",
          description:
            "A primer on natural perfumery, ingredient portraits, bottle mythologies, and the character archive.",
          publisher: { "@type": "Organization", name: "House of Apocrypha", url: "https://houseofapocrypha.com/" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://houseofapocrypha.com/" },
            { "@type": "ListItem", position: 2, name: "Field Notes", item: "https://houseofapocrypha.com/field-notes" },
          ],
        }),
      },
    ],
  }),
  component: FieldNotesIndex,
});

function FieldNotesIndex() {
  return (
    <div className="min-h-screen bg-background">
      <PageHeader />
      <main className="mx-auto max-w-6xl px-6 py-20 lg:py-28">
        <div className="text-center">
          <p className="eyebrow">From the Workshop</p>
          <h1 className="mt-6 text-4xl text-parchment sm:text-5xl lg:text-6xl">
            <span className="italic text-gold/90">Field</span> Notes
          </h1>
          <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
            Marginalia from the workshop — primers, materials, stories, characters. A slow archive
            of the practice behind every bottle.
          </p>
          <div className="ornament mt-10">
            <span>✶</span>
          </div>
        </div>

        <div className="mt-20 grid gap-10 sm:grid-cols-2">
          {sections.map((s, i) => {
            const linkProps = s.params
              ? ({ to: s.to, params: s.params } as const)
              : ({ to: s.to } as const);
            return (
              <motion.article
                key={s.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="parchment group relative flex flex-col border border-border p-8 text-[oklch(0.26_0.03_60)]"
              >
                <span
                  aria-hidden
                  className="absolute right-6 top-6 font-display text-[0.65rem] uppercase tracking-[0.28em] text-[oklch(0.38_0.04_60)]/70"
                >
                  {s.numeral}
                </span>

                <div className="flex items-center gap-3 text-[oklch(0.32_0.04_60)]">
                  <Sigil kind={s.sigil} />
                  <p className="font-display text-xs uppercase tracking-[0.28em] text-[oklch(0.38_0.04_60)]">
                    {s.subtitle}
                  </p>
                </div>
                <h2 className="mt-3 font-display text-3xl text-[oklch(0.22_0.03_60)]">{s.title}</h2>
                <p className="mt-5 leading-relaxed text-[oklch(0.32_0.03_60)]">{s.description}</p>
                <div className="mt-8">
                  <Link
                    {...linkProps}
                    className="inline-block border-b border-[oklch(0.26_0.03_60)]/40 pb-1 font-display text-xs uppercase tracking-[0.24em] text-[oklch(0.26_0.03_60)] transition-colors hover:border-[oklch(0.26_0.03_60)]"
                  >
                    {s.cta} →
                  </Link>
                </div>
              </motion.article>
            );
          })}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
